import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../store/authStore";
import { TEST_IDS } from "../constants/testIds";
import { formatApiErrorDetail, api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/ui/password-input";
import { SerpixWordmark } from "../components/brand/SerpixWordmark";
import { Sparkles, Lock } from "lucide-react";

const DEMO_CREDENTIALS = { email: "user1@serpixtest.com", password: "1453Abc&" };

export default function Login() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  // Platform-wide login lock — ultra_admin can panic-close the app.
  // We fetch the status once on mount (public endpoint, no auth
  // required) and render a red banner with the operator-set reason
  // so users don't waste time trying to log in.
  const [systemLock, setSystemLock] = useState(null);

  useEffect(() => {
    api
      .get("/system/lock")
      .then(({ data }) => setSystemLock(data))
      .catch(() => setSystemLock(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.user.role === "platform_admin") nav("/app/platform");
      else nav("/app/dashboard");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const tryDemo = async () => {
    setError("");
    setDemoLoading(true);
    try {
      const data = await login(DEMO_CREDENTIALS.email, DEMO_CREDENTIALS.password);
      try {
        sessionStorage.setItem("serpix_demo_tour", "1");
      } catch {
        /* ignore */
      }
      if (data.user.role === "platform_admin") nav("/app/platform");
      else nav("/app/dashboard");
    } catch (e) {
      setError(
        formatApiErrorDetail(e.response?.data?.detail) ||
          t("auth.demo_error") ||
          e.message,
      );
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden bg-zinc-100">
        <img
          src="https://images.pexels.com/photos/12732218/pexels-photo-12732218.jpeg"
          alt=""
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 inline-flex items-center gap-2">
            <SerpixWordmark size="sm" />
          </Link>
          <h1 className="mb-2 font-display text-3xl font-semibold text-zinc-950">
            {t("auth.login")}
          </h1>
          <p className="mb-8 text-sm text-zinc-500">
            {t("auth.no_account")}{" "}
            <Link to="/register" className="text-zinc-900 underline underline-offset-4">
              {t("auth.register")}
            </Link>
          </p>

          {systemLock?.locked && (
            <div
              data-testid="login-system-lock-banner"
              className="mb-6 flex gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
            >
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold">Sistem bakımda</div>
                <div className="mt-0.5 text-[13px] text-red-800/90">
                  {systemLock.reason?.trim() ||
                    "Şu an giriş yapılamıyor. Lütfen daha sonra tekrar deneyin."}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.email")}
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={TEST_IDS.loginEmail}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.password")}
              </Label>
              <PasswordInput
                id="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={TEST_IDS.loginPassword}
                testIdToggle="login-password-toggle"
                className="mt-1"
              />
            </div>
            {error && (
              <div
                data-testid={TEST_IDS.loginError}
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid={TEST_IDS.loginSubmit}
              className="w-full bg-gradient-to-r from-[#5B4CFB] to-[#7c5cf6] text-white shadow-lg shadow-violet-500/25 hover:from-[#4f40f0] hover:to-[#6f4df0]"
            >
              {loading ? "…" : t("auth.login")}
            </Button>
          </form>

          {/* Demo access — one-click login for evaluators */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-zinc-200" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
              <span className="bg-white px-3 text-zinc-400">{t("auth.or")}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={tryDemo}
            disabled={demoLoading || loading}
            data-testid="login-demo-btn"
            className="group flex w-full items-center justify-center gap-2 rounded-md border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-indigo-50 px-4 py-2.5 text-sm font-semibold text-violet-900 transition-all hover:border-violet-300 hover:from-violet-100 hover:to-indigo-100 hover:shadow-md hover:shadow-violet-500/10 disabled:opacity-60"
          >
            <Sparkles className="h-4 w-4 text-violet-600 transition-transform group-hover:rotate-12" />
            {demoLoading ? t("common.loading") : t("auth.try_demo")}
          </button>
          <p className="mt-1.5 text-center text-[11px] text-zinc-500">
            {t("auth.try_demo_hint")}
          </p>

          <div className="mt-6 flex justify-between text-xs">
            <Link to="/forgot-password" className="text-zinc-500 hover:text-zinc-900">
              {t("auth.forgot")}
            </Link>
            <Link to="/accept-invite" className="text-zinc-500 hover:text-zinc-900">
              {t("auth.accept_invite")}
            </Link>
          </div>
          <div className="mt-2 text-center text-[11px]">
            <Link
              to="/recovery"
              data-testid="login-recovery-link"
              className="text-zinc-400 hover:text-violet-700"
            >
              Ultra Yönetici kurtarma kodu ile giriş →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
